import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, User, ShoppingCart, X, Menu } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Collections', path: '/collections' },
  { label: 'Contact', path: '/contact' },
  { label: 'Artist info', path: '/artist' },
];

const SiteHeader = ({ onCartClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cart } = useCart();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/collections?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setMobileNavOpen(false);
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-40 bg-[#09090B]/90 backdrop-blur-xl border-b border-[#27272A]" data-testid="site-header">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0 flex items-center" data-testid="site-logo">
          <img
            src="/kehele-keff-logo.webp"
            alt="Kehelekeff"
            className="h-9 md:h-10 w-auto object-contain select-none"
            draggable="false"
          />
        </Link>

        {/* Center Nav (desktop) */}
        <nav className="hidden md:flex items-center gap-8 text-sm">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`transition-colors ${isActive(link.path) ? 'text-white underline underline-offset-4 decoration-[#00F0FF]' : 'text-[#A1A1AA] hover:text-white'}`}
              data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-2">
          {searchOpen ? (
            <form onSubmit={handleSearchSubmit} className="flex items-center gap-1" data-testid="search-form">
              <input
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search title, artist, genre..."
                className="w-48 md:w-64 h-9 px-3 bg-[#18181B] border border-[#27272A] rounded-full text-sm text-white placeholder:text-[#A1A1AA] focus:outline-none focus:border-[#00F0FF]"
                data-testid="search-input"
              />
              <button
                type="button"
                onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                className="w-9 h-9 rounded-full text-[#A1A1AA] hover:text-white hover:bg-[#18181B] flex items-center justify-center transition-colors"
              >
                <X size={18} />
              </button>
            </form>
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className="w-9 h-9 rounded-full text-[#A1A1AA] hover:text-white hover:bg-[#18181B] flex items-center justify-center transition-colors"
              data-testid="search-toggle-button"
              aria-label="Search"
            >
              <Search size={18} />
            </button>
          )}

          <button
            onClick={() => toast.info('Buyer sign in coming soon')}
            className="w-9 h-9 rounded-full text-[#A1A1AA] hover:text-white hover:bg-[#18181B] flex items-center justify-center transition-colors"
            data-testid="signin-button"
            aria-label="Sign in"
          >
            <User size={18} />
          </button>

          <button
            onClick={onCartClick}
            className="relative w-9 h-9 rounded-full text-[#A1A1AA] hover:text-white hover:bg-[#18181B] flex items-center justify-center transition-colors"
            data-testid="cart-button"
            aria-label="Cart"
          >
            <ShoppingCart size={18} />
            {cart.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#00F0FF] text-black text-[10px] font-semibold rounded-full flex items-center justify-center" data-testid="cart-badge">
                {cart.length}
              </span>
            )}
          </button>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="md:hidden w-9 h-9 rounded-full text-[#A1A1AA] hover:text-white hover:bg-[#18181B] flex items-center justify-center transition-colors"
            aria-label="Menu"
          >
            {mobileNavOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav Drawer */}
      {mobileNavOpen && (
        <nav className="md:hidden border-t border-[#27272A] px-6 py-4 flex flex-col gap-3">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileNavOpen(false)}
              className={`text-sm ${isActive(link.path) ? 'text-white' : 'text-[#A1A1AA]'}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
};

export default SiteHeader;
